/**
 * Client-side parser for raw L2 .txt DAT exports. Runs entirely in the browser —
 * no server involvement. Auto-detects the data type from the line prefix and
 * parses records into a normalized shape for local searching.
 *
 * The prefixes and field mappings mirror the server-side parser
 * (l2-localization-server/models/utils/parseFactory.js) so detection is reliable.
 */

// Known formats, checked in order. `prefix` is what a data line starts with.
const FORMATS = [
  {
    type: 'Item',
    prefix: 'item_name_begin',
    mode: 'keyValue',
    idKey: 'id',
    primaryKey: 'name',
    secondaryKey: 'description',
    primaryLabel: 'Name',
    secondaryLabel: 'Description',
  },
  {
    type: 'Skill',
    prefix: 'skill_begin',
    mode: 'keyValue',
    idKey: 'skill_id',
    primaryKey: 'name',
    secondaryKey: 'desc',
    levelKey: 'skill_level',
    sublevelKey: 'skill_sublevel',
    primaryLabel: 'Name',
    secondaryLabel: 'Description',
  },
  {
    type: 'Enchanted Item Skill',
    prefix: 'enchanted_item_skill_begin',
    mode: 'enchanted',
    primaryLabel: 'Text',
    secondaryLabel: 'All levels',
  },
  {
    type: 'NPC Name',
    prefix: 'npc_begin',
    mode: 'indexed',
    indexMap: {
      id: { index: 1, splitBy: '=' },
      name: { index: 2, splitBy: '=[', brackets: true },
      nick: { index: 3, splitBy: '=[', brackets: true },
      nickColor: { index: 4, splitBy: '=' },
    },
    idKey: 'id',
    primaryKey: 'name',
    secondaryKey: 'nick',
    primaryLabel: 'Name',
    secondaryLabel: 'Nickname',
  },
  {
    type: 'String',
    prefix: 'string_begin',
    mode: 'indexed',
    indexMap: {
      id: { index: 1, splitBy: '=' },
      string: { index: 2, splitBy: '=[', brackets: true },
    },
    idKey: 'id',
    primaryKey: 'string',
    secondaryKey: null,
    primaryLabel: 'String',
    secondaryLabel: '',
  },
  {
    type: 'Hunting Zone',
    prefix: 'Hunt_begin',
    mode: 'indexed',
    indexMap: {
      id: { index: 1, splitBy: '=' },
      string: { index: 7, splitBy: '=[', brackets: true },
    },
    idKey: 'id',
    primaryKey: 'string',
    secondaryKey: null,
    primaryLabel: 'Zone',
    secondaryLabel: '',
  },
];

function detectFormat(lines) {
  for (const raw of lines) {
    const line = raw.trimStart();
    if (!line) continue;
    for (const fmt of FORMATS) {
      if (line.startsWith(fmt.prefix)) return fmt;
    }
  }
  return null;
}

function toRecord(fields, fmt) {
  return {
    id: fields[fmt.idKey],
    primary: fmt.primaryKey ? fields[fmt.primaryKey] || '' : '',
    secondary: fmt.secondaryKey ? fields[fmt.secondaryKey] || '' : '',
    level: fmt.levelKey ? fields[fmt.levelKey] : undefined,
    sublevel: fmt.sublevelKey ? fields[fmt.sublevelKey] : undefined,
    fields,
  };
}

function parseKeyValue(lines, fmt) {
  const records = [];
  for (const raw of lines) {
    const line = raw.trimStart();
    if (!line.startsWith(fmt.prefix)) continue;

    const fields = {};
    line.split('\t').forEach((prop) => {
      const i = prop.indexOf('=');
      if (i === -1) return;
      const key = prop.substring(0, i);
      const value = prop.substring(i + 1).replace(/^\[|\]$/g, '');
      fields[key] = value;
    });
    records.push(toRecord(fields, fmt));
  }
  return records;
}

function parseIndexed(lines, fmt) {
  const records = [];
  for (const raw of lines) {
    const line = raw.trimStart();
    if (!line.startsWith(fmt.prefix)) continue;

    const props = line.split('\t');
    const fields = {};
    Object.entries(fmt.indexMap).forEach(([field, cfg]) => {
      const prop = props[cfg.index];
      if (!prop) return;
      let value = prop.split(cfg.splitBy)[1];
      if (value && cfg.brackets) value = value.replace(/^\[|\]$/g, '');
      fields[field] = value;
    });
    records.push(toRecord(fields, fmt));
  }
  return records;
}

// Enchanted item skills are nested: one item block (item_id) holds several
// `desc_begin … desc_end` sub-blocks, one per RequiredEnchant level, each with a
// translatable `text` field. Emit ONE record per level, keyed by
// `item_id|RequiredEnchant`, so a level's text can be looked up unambiguously.
function parseEnchanted(lines, fmt) {
  const records = [];
  let itemId = '';
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;
    const tokens = line.split('\t');

    // A begin line carries the item_id (and usually the first desc block too).
    const idTok = tokens.find((t) => t.startsWith('item_id='));
    if (idTok) itemId = idTok.substring('item_id='.length).trim();

    // Any line with a `text=` token is one enchant level.
    const textTok = tokens.find((t) => t.startsWith('text='));
    if (textTok) {
      const reqTok = tokens.find((t) => t.startsWith('RequiredEnchant='));
      const req = reqTok ? reqTok.substring('RequiredEnchant='.length).trim() : '';
      const text = textTok.substring('text='.length).replace(/^\[|\]$/g, '');
      records.push({
        id: itemId,
        reqEnchant: req,
        key: `${itemId}|${req}`,
        primary: text,
        secondary: '',
        fields: { item_id: itemId, RequiredEnchant: req, text },
      });
    }
  }
  return records;
}

/**
 * Parse raw file text. Returns { type, records, format }.
 * type === 'Unknown' when no known format prefix is found.
 */
export function parseData(text) {
  const lines = (text || '').split(/\r?\n/);
  const fmt = detectFormat(lines);
  if (!fmt) {
    return { type: 'Unknown', records: [], format: null };
  }

  let records;
  if (fmt.mode === 'keyValue') records = parseKeyValue(lines, fmt);
  else if (fmt.mode === 'indexed') records = parseIndexed(lines, fmt);
  else records = parseEnchanted(lines, fmt);

  return { type: fmt.type, records, format: fmt };
}

/**
 * Filter + rank records by a query string. Matches ID and text fields; closest
 * matches (exact id, id prefix, exact/prefix text, then substrings) come first.
 */
export function filterRecords(records, query, limit = 300) {
  const q = (query || '').trim().toLowerCase();
  if (!q) return records.slice(0, limit);

  const scored = [];
  for (const r of records) {
    const idStr = String(r.id == null ? '' : r.id).toLowerCase();
    const primary = (r.primary || '').toLowerCase();
    const secondary = (r.secondary || '').toLowerCase();

    let score = null;
    if (idStr === q) score = 0;
    else if (idStr.startsWith(q)) score = 1;
    else if (primary === q) score = 1;
    else if (primary.startsWith(q)) score = 2;
    else if (primary.includes(q)) score = 3;
    else if (secondary.includes(q)) score = 4;
    else if (idStr.includes(q)) score = 5;

    if (score !== null) scored.push([score, r]);
  }

  scored.sort(
    (a, b) => a[0] - b[0] || (a[1].primary || '').length - (b[1].primary || '').length
  );
  return scored.slice(0, limit).map((s) => s[1]);
}
